import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {FACT_TYPE_MODIFIER} from '../../memory/factConstants';

// Cuántos hechos entran a la esfera. Más que esto y las etiquetas se pisan entre sí: la nube deja
// de leerse como "esto es lo que sabe" y pasa a ser una mancha.
//
// Medido en pantalla con un cliente real (hechos de hasta 130 caracteres): con 14 etiquetas sin
// recortar, la más larga medía **905 px** y el promedio **391 px** — contra una esfera de 192 px de
// diámetro. La nube era un bloque de texto con tres pares de etiquetas nítidas encimadas.
const MAX_TAGS = 10;

// Cuánto de un hecho entra en una etiqueta. No es un límite estético: es lo que hace que la
// etiqueta quepa en el orbe. El hecho COMPLETO viaja en el `title`, y la versión entera está a un
// scroll de distancia, en el archivo del acto Memoria — la nube muestra «algo de lo que ya sabe»,
// no es donde se leen los hechos.
const MAX_CHARS = 40;

// Lo que entra en una pastilla, en caracteres. NO es un número estético: `max-width: 19em` a 0,62rem
// son ~188 px de contenido, y a la tipografía de la nube eso da 38 caracteres. Es el MISMO número
// que `SHORT_LABEL_MAX_CHARS` en el backend, a propósito: el QA del 09/09 encontró que el backend
// aceptaba hasta 48 y el CSS después lo mordía a mitad de palabra —«Objetivo trimestral consolidado
// unida…»—, que es exactamente lo que el tope del backend existe para evitar.
//
// Acá se vuelve a chequear igual, y no por desconfianza: es la única capa que conoce la caja. El día
// que el tope del backend suba, la nube cae al recorte en vez de dibujar un rótulo mordido.
const MAX_LABEL_CHARS = 38;

// La esfera angosta. A ≤620px la caja pasa a 240×190 (`_ia-booster-feed.scss`) pero el radio y la
// cantidad de etiquetas eran fijos: medido por el QA a 390 px, 5,2 de las 10 etiquetas quedaban
// fuera de la caja y 0,6 se salían de la pantalla, cortadas contra el borde. El radio y el cupo
// bajan con la caja.
const NARROW_QUERY = '(max-width: 620px)';
const NARROW_RADIUS = 68;
const NARROW_MAX_TAGS = 7;

function isNarrow() {
    return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia(NARROW_QUERY).matches;
}

/**
 * Recorta por PALABRA, nunca a mitad de una. Con corte duro («La marca Carolina Herrera, del prov…»)
 * la etiqueta parece rota; cortando por palabra se lee como una frase que sigue.
 */
export function shortenFact(text, max = MAX_CHARS) {
    const clean = String(text || '').trim();
    if (clean.length <= max) return clean;
    const cut = clean.slice(0, max);
    const lastSpace = cut.lastIndexOf(' ');
    // Sin espacios en el tramo (una URL, un nombre larguísimo) el corte duro es la única salida.
    return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.—-]+$/, '')}…`;
}

// Radio de la esfera, en píxeles. La caja del orbe mide 400×240, así que este radio la llena sin
// que las etiquetas del ecuador se salgan por los costados.
const RADIUS = 96;

// Duración de una vuelta completa. Espeja el `animation-duration` de `.iab-cloud-sphere` en el
// SCSS: el desfasaje de opacidad de cada etiqueta se calcula contra este número, así que si los dos
// valores se separan, las etiquetas se apagan cuando están al frente.
const SPIN_SECONDS = 26;

const GOLDEN_ANGLE = Math.PI * (1 + Math.sqrt(5));

/**
 * Un hecho puede llegar como string suelto o como el objeto que devuelve el brief.
 * Se normaliza acá y no en el consumidor: la nube es la única que lee esta forma.
 */
function statementOf(fact) {
    if (typeof fact === 'string') return fact.trim();
    if (!fact || typeof fact !== 'object') return '';
    return String(fact.statement || fact.text || '').trim();
}

/**
 * Lo que se DIBUJA en la etiqueta.
 *
 * Desde el 08/09 el extractor emite un `short_label` por hecho: 2 o 3 palabras que RESUMEN de qué
 * se trata («CAC sobre awareness»), no sus primeras palabras. Es lo que hace que la esfera se lea
 * como un mapa de conceptos y no como una pila de frases cortadas.
 *
 * El recorte sigue vivo como CAÍDA y no como transición: lo usan los hechos anteriores al campo
 * (hasta que corra el backfill), los que el modelo no rotuló, y los que llegan como string suelto.
 * Ese camino no se va a poder borrar — un hecho editado a mano suelta su rótulo a propósito.
 */
function labelOf(fact) {
    const label = shortLabelOf(fact);
    if (label) return label;
    return shortenFact(statementOf(fact));
}

/**
 * El rótulo del backend, sólo si ENTRA. Uno que no entra se descarta entero y el hecho cae al
 * recorte del statement: nunca se muerde un rótulo, que es la convención del frente («entra entero
 * o no entra») y la razón por la que el backend descarta en vez de recortar.
 */
function shortLabelOf(fact) {
    if (!fact || typeof fact !== 'object' || !fact.short_label) return '';
    const label = String(fact.short_label).trim();
    return label && label.length <= MAX_LABEL_CHARS ? label : '';
}

/**
 * El color de la etiqueta sale del TIPO del hecho, con el mismo modificador que la barra de la
 * carta y el título de su grupo en el archivo (`FACT_TYPE_MODIFIER`). Es la misma señal en las tres
 * superficies: si el mapa gana un tipo, la nube lo hereda sin tocar nada acá.
 */
function typeModifierOf(fact) {
    if (!fact || typeof fact !== 'object') return '';
    return FACT_TYPE_MODIFIER[fact.fact_type] || '';
}

/**
 * Reparto de N puntos sobre una esfera con la espiral de Fibonacci.
 *
 * Es la misma fórmula del mockup (`phi = acos(1 - 2(i+.5)/n)`, `theta = π(1+√5)(i+.5)`): reparte
 * parejo y sin cúmulos, que es lo que un `Math.random()` por punto no garantiza — con 14 etiquetas
 * al azar siempre quedan tres pegadas y un hueco.
 *
 * El mockup lo dibuja proyectando a mano en un rAF; acá la proyección la hace el navegador con
 * `perspective` + `translateZ`, así que de cada punto sólo se necesitan los DOS ÁNGULOS que lo
 * colocan (azimut y elevación) y su profundidad inicial, para el caso sin animación.
 */
export function spherePlacements(count, {radius = RADIUS, spinSeconds = SPIN_SECONDS} = {}) {
    const placements = [];
    for (let i = 0; i < count; i++) {
        const polar = Math.acos(1 - 2 * (i + 0.5) / count);
        const azimuth = GOLDEN_ANGLE * (i + 0.5);
        // `polar` va del polo norte (0) al sur (π); CSS rota en elevación desde el ecuador.
        const elevationDeg = 90 - (polar * 180) / Math.PI;
        const azimuthDeg = ((azimuth * 180) / Math.PI) % 360;
        // La profundidad de una etiqueta al girar la esfera es `cos(t + azimut)`: su fase depende
        // sólo del azimut, así que el vaivén de opacidad se consigue con un `animation-delay`
        // negativo por etiqueta en vez de recalcular 14 opacidades en cada frame.
        const depthDelay = -(azimuthDeg / 360) * spinSeconds;
        // Profundidad en reposo, normalizada a [0, 1]: la `z` del punto antes de que la esfera gire
        // (`cos(elevación) · cos(azimut)`). Sólo se usa cuando la animación está apagada.
        const restDepth = (Math.cos((elevationDeg * Math.PI) / 180)
            * Math.cos((azimuthDeg * Math.PI) / 180) + 1) / 2;
        placements.push({
            azimuthDeg,
            elevationDeg,
            radius,
            depthDelay,
            restOpacity: Number((0.35 + 0.65 * restDepth).toFixed(2)),
        });
    }
    return placements;
}

/**
 * La nube de hechos: la segunda vista del organismo.
 *
 * Muestra lo que la memoria YA sabe, flotando alrededor del cliente. Es la única de las tres vistas
 * del carrusel que puede no estar: si el brief todavía no llegó —o llegó vacío— no hay nada que
 * hacer girar, y una esfera vacía diría que este cliente no sabe nada.
 *
 * **Va en CSS 3D, no en canvas.** El mockup la dibuja en un `<canvas>` con su propio rAF; acá los
 * hechos son TEXTO REAL en el DOM, así que se pueden leer con un lector de pantalla, se seleccionan
 * y no hay un segundo bucle de animación compitiendo con el del orbe. Tres capas por etiqueta:
 *
 *   `.iab-cloud-pos`  la coloca sobre la esfera (transform estático)
 *   `.iab-cloud-face` deshace esa rotación para que el texto quede de frente
 *   `.iab-cloud-tag`  deshace la rotación de la ESFERA (misma duración, sentido contrario)
 *
 * Las tres rotaciones se cancelan, así que la etiqueta viaja con la esfera pero nunca se ve de
 * canto ni espejada. Con `prefers-reduced-motion` las dos animaciones se apagan en el SCSS y queda
 * la foto: cada etiqueta en su lugar, con la opacidad de su profundidad en reposo (el `opacity`
 * inline, que sólo gana cuando no hay animación corriendo).
 */
export function OrbFactCloud({facts, max = MAX_TAGS}) {
    const {t} = useTranslation();
    const narrow = isNarrow();

    const tags = useMemo(() => {
        const cupo = narrow ? Math.min(max, NARROW_MAX_TAGS) : max;
        const radius = narrow ? NARROW_RADIUS : RADIUS;
        // El filtro va sobre el TEXTO A DIBUJAR, no sobre el hecho: un hecho sin statement y sin
        // rótulo no tiene nada que mostrar y ocuparía un punto de la esfera con una etiqueta vacía.
        const dibujables = (facts || [])
            .map((fact) => ({text: labelOf(fact), full: statementOf(fact),
                             modifier: typeModifierOf(fact), rotulado: !!shortLabelOf(fact)}))
            .filter((tag) => tag.text);
        // Los ROTULADOS primero, y estable dentro de cada grupo (los hechos ya vienen ordenados por
        // recencia). Los 10 lugares se llenaban por puro orden de llegada, así que un backfill a
        // medias —o el estado permanente, con hechos viejos conviviendo con nuevos— mezclaba
        // pastillas de 70 px con otras de 200. Medido por el QA: las largas producen TODO el solape.
        // Es la palanca más barata contra el encimado y no necesita nada del backend.
        const drawable = [...dibujables.filter((tag) => tag.rotulado),
                          ...dibujables.filter((tag) => !tag.rotulado)].slice(0, cupo);
        const placements = spherePlacements(drawable.length, {radius});
        return drawable.map((tag, i) => ({...tag, full: tag.full || tag.text, ...placements[i]}));
    }, [facts, max, narrow]);

    if (!tags.length) return null;

    return (
        <div className="iab-cloud">
            <ul className="iab-cloud-sphere">
                {tags.map((tag, i) => (
                    <li
                        key={`${i}-${tag.text}`}
                        className="iab-cloud-pos"
                        style={{
                            transform: `rotateY(${tag.azimuthDeg.toFixed(2)}deg) `
                                + `rotateX(${tag.elevationDeg.toFixed(2)}deg) `
                                + `translateZ(${tag.radius}px)`,
                        }}
                    >
                        <span
                            className="iab-cloud-face"
                            style={{
                                transform: `rotateX(${(-tag.elevationDeg).toFixed(2)}deg) `
                                    + `rotateY(${(-tag.azimuthDeg).toFixed(2)}deg)`,
                            }}
                        >
                            {/* Los dos delays van en el MISMO orden que las dos animaciones del
                                SCSS: la que desgira (sincronizada con la esfera, delay 0) y la de
                                profundidad (desfasada por el azimut de esta etiqueta). */}
                            <span
                                className={`iab-cloud-tag ${tag.modifier}`.trim()}
                                // El hecho entero: el rótulo lo RESUME, así que lo que resume
                                // sigue estando a un hover de distancia.
                                title={tag.full}
                                style={{
                                    animationDelay: `0s, ${tag.depthDelay.toFixed(2)}s`,
                                    opacity: tag.restOpacity,
                                }}
                            >
                                {tag.text}
                            </span>
                        </span>
                    </li>
                ))}
            </ul>
            <p className="iab-cloud-cap">{t('ia_booster.feed.cloud_caption')}</p>
        </div>
    );
}

export default OrbFactCloud;
